import { useEffect, useState } from 'react';
import { employesApi } from '../../api/resources';
import { delaiEnMois } from '../../lib/contrat';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const iso = (d) =>
  d.getFullYear() +
  '-' +
  String(d.getMonth() + 1).padStart(2, '0') +
  '-' +
  String(d.getDate()).padStart(2, '0');

/**
 * La démission reçue, côté bureau : accuser réception, fixer la date de
 * départ (proposée selon le délai), et préparer le certificat de travail.
 *
 * @param {{emp: object, setEmployes?: Function, onCertificat?: (dates: {entree: string, sortie: string}) => void}} props
 */
export function DemissionRecue({ emp, setEmployes, onCertificat }) {
  const [lettre, setLettre] = useState(null);
  const [depart, setDepart] = useState('');
  const [etat, setEtat] = useState(null);

  useEffect(() => {
    if (!Number.isInteger(emp?.id) || typeof employesApi.documents !== 'function') return undefined;
    let actif = true;
    Promise.resolve()
      .then(() => employesApi.documents(emp.id))
      .then((liste) => {
        if (!actif) return;
        const d =
          (Array.isArray(liste) ? liste : []).find((x) => x.type === 'LETTRE_DEMISSION') || null;
        setLettre(d);
        if (d) {
          const recue = d.emisLe ? new Date(d.emisLe) : new Date();
          const propose = new Date(
            recue.getFullYear(),
            recue.getMonth() + delaiEnMois(emp, recue) + 1,
            0,
          );
          setDepart(emp.dateDepart || iso(propose));
        }
      })
      .catch(() => {});
    return () => {
      actif = false;
    };
    // Relue quand on change d'employé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emp?.id]);

  if (!lettre) return null;
  const dejaInscrit = emp.dateDepart && emp.motifDepart;

  const accuse = () => {
    if (!depart) return;
    setEtat('envoi');
    const texte = trf(
      'Nous avons bien reçu ta lettre de démission. Ton dernier jour de travail : {0}. Ton certificat de travail te sera remis dans « Mes documents ».',
      dateSuisse(depart),
    );
    employesApi
      .ecrit(emp.id, texte)
      .then(() => {
        if (setEmployes) {
          setEmployes((p) =>
            p.map((e) =>
              e.id === emp.id ? { ...e, dateDepart: depart, motifDepart: 'Démission' } : e,
            ),
          );
        }
        setEtat('ok');
      })
      .catch(() => setEtat('erreur'));
  };

  return (
    <div
      style={{
        border: '2px solid ' + C.purple,
        borderRadius: 12,
        padding: 12,
        margin: '10px 0',
        display: 'grid',
        gap: 8,
        background: C.white,
      }}
    >
      <div style={{ fontSize: 12.5, fontWeight: 800 }}>
        {trf('✍️ Démission reçue le {0}', dateSuisse(lettre.emisLe))}
      </div>
      <label style={{ display: 'grid', gap: 3, fontSize: 10.5, fontWeight: 700 }}>
        {tr('Dernier jour de travail')}
        <input
          type="date"
          value={depart}
          onChange={(e) => setDepart(e.target.value)}
          style={{ border: '1px solid ' + C.border, borderRadius: 8, padding: 8, fontSize: 12 }}
        />
      </label>
      <div style={{ fontSize: 10, color: C.muted }}>
        {trf(
          'Proposé selon son délai de congé : {0} mois, pour la fin d’un mois.',
          delaiEnMois(emp),
        )}
      </div>
      {etat === 'ok' || dejaInscrit ? (
        <div role="status" style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>
          {trf(
            '✓ Réception confirmée. Date de départ inscrite : {0}.',
            dateSuisse(emp.dateDepart || depart),
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={!depart || etat === 'envoi'}
          onClick={accuse}
          style={{
            minHeight: 42,
            borderRadius: 10,
            border: 'none',
            background: C.green,
            color: C.white,
            fontWeight: 800,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {tr('✓ Accuser réception et inscrire la date de départ')}
        </button>
      )}
      {etat === 'erreur' && (
        <div role="alert" style={{ color: C.red, fontSize: 11 }}>
          {tr('Envoi impossible, réessaie.')}
        </div>
      )}
      {onCertificat && (
        <button
          type="button"
          onClick={() => onCertificat({ entree: emp.dateEmbauche || '', sortie: depart })}
          style={{
            minHeight: 42,
            borderRadius: 10,
            border: '1.5px solid ' + C.green,
            background: C.white,
            color: C.green,
            fontWeight: 800,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {tr('📜 Préparer son certificat de travail')}
        </button>
      )}
    </div>
  );
}
